import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Eye,
  Calendar,
  Edit,
  Trash2,
  Users,
  Filter,
  Search,
} from "lucide-react";
import { trainersAPI } from "../services/api";
import { useApp } from "../contexts/AppContext";
import { useTheme } from "../contexts/ThemeContext";
import TrainerModal from "../components/ui/TrainerModal";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { useNavigate } from "react-router-dom";
import { useCRUD } from "../hooks/useCRUD";
import { useTableData } from "../hooks/useTableData";
import { useFilters } from "../hooks/useFilters";
import { useWindowWidth } from "../hooks/useWindowWidth";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable/DataTable";
import FilterSidebar from "../components/common/FilterSidebar";
import { getInitials, getAvatarColor } from "../utils/avatarHelpers";
import { usePermissions } from "../components/HasPermission";

const TrainersPage = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const {
    selectedOrganizationId,
    organizations,
    selectedOrganization,
    isSuperAdmin,
  } = useApp();

  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const { hasPermission } = usePermissions();

  // Modular hooks for CRUD operations
  const {
    data: trainers,
    setData: setTrainers,
    loading,
    actionLoading,
    error,
    setError,
    successMessage,
    setSuccessMessage,
    fetchAll: fetchTrainers,
    create: createTrainer,
    update: updateTrainer,
    remove: removeTrainer,
  } = useCRUD(trainersAPI, selectedOrganizationId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    trainer: null,
  });
  const [viewMode] = useState("organization");
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);

  // Filter configuration
  const filterConfigs = [
    {
      key: "status",
      type: "exact",
      accessor: (item) => item.status,
      defaultValue: "active",
    },
    {
      key: "role",
      type: "exact",
      accessor: (item) => item.role,
      defaultValue: "all",
    },
    {
      key: "organization",
      type: "custom",
      accessor: (item) => item.organizationId,
      filterFn: (item, filterValue) => {
        if (filterValue === "all") return true;
        return (
          item.organizationId === filterValue ||
          item.assignedOrganizations?.some((org) => org.id === filterValue)
        );
      },
      defaultValue: "all",
    },
  ];

  // Hide inactive trainers from non-super-admin users
  const visibleTrainers = useMemo(
    () =>
      isSuperAdmin()
        ? trainers
        : trainers.filter((t) => t.status === "active"),
    [trainers, isSuperAdmin],
  );

  // Use filters hook
  const {
    filters,
    filteredData: filterByFilters,
    setFilter,
    resetFilters,
  } = useFilters(visibleTrainers, filterConfigs);

  // Use table data hook with search
  const {
    paginatedData: paginatedTrainers,
    totalItems: filteredCount,
    currentPage,
    rowsPerPage,
    totalPages,
    startIndex,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = useTableData(filterByFilters, ["name", "email", "phone"]);

  // Fetch data on mount and when organization or viewMode changes
  useEffect(() => {
    if (selectedOrganizationId) {
      fetchTrainers();
    }
  }, [selectedOrganizationId, viewMode, fetchTrainers]);

  // CRUD handlers
  const handleCreate = async (trainerData) => {
    try {
      if (isSuperAdmin() && trainerData.assignedOrganizations?.length > 0) {
        let createdTrainers = [];
        let hasErrors = false;
        let errorMessages = [];

        for (const orgId of trainerData.assignedOrganizations) {
          try {
            const response = await trainersAPI.create(orgId, {
              ...trainerData,
              assignedOrganizations: undefined,
            });
            createdTrainers.push({
              ...response.data.data,
              organizationId: orgId,
            });
          } catch (err) {
            hasErrors = true;
            const org = organizations.find((o) => o.id === orgId);
            errorMessages.push(
              `Failed to create trainer in ${org?.name || orgId}: ${
                err.response?.data?.error || err.message
              }`,
            );
          }
        }

        if (createdTrainers.length > 0) {
          // Deduplicate trainers by ID before adding
          setTrainers((prev) => {
            const existingIds = new Set(prev.map((t) => t.id));
            const newTrainers = createdTrainers.filter(
              (t) => !existingIds.has(t.id),
            );
            // Also deduplicate within newTrainers (in case same trainer created for multiple orgs)
            const uniqueNewTrainers = newTrainers.filter(
              (trainer, index, self) =>
                index === self.findIndex((t) => t.id === trainer.id),
            );
            return [...prev, ...uniqueNewTrainers];
          });
          setIsModalOpen(false);

          if (hasErrors) {
            setSuccessMessage({
              type: "warning",
              title: "Trainer Created with Some Issues",
              message: `${trainerData.name} was created in ${createdTrainers.length} organization(s), but there were issues with ${errorMessages.length} organization(s).`,
              warnings: errorMessages,
            });
          } else {
            setSuccessMessage(
              `${trainerData.name} has been created and assigned to ${createdTrainers.length} organization(s).`,
            );
          }
        } else {
          setError("Failed to create trainer in any organization.");
        }
      } else {
        const orgId =
          selectedOrganizationId ||
          selectedOrganization?.id ||
          trainerData.assignedOrganizations?.[0];

        if (!orgId) {
          setError(
            "No organization selected for trainer creation. Please select an organization first.",
          );
          return;
        }

        const result = await createTrainer(
          {
            ...trainerData,
            assignedOrganizations: undefined,
          },
          `${trainerData.name} has been created successfully.`,
        );

        if (result) {
          setIsModalOpen(false);
        }
      }
    } catch (err) {
      console.error("Error creating trainer:", err);
      if (err.response?.status === 409) {
        setError("A trainer with this email already exists in the system.");
      } else {
        setError(
          `Failed to create trainer: ${
            err.response?.data?.error || err.message
          }`,
        );
      }
    }
  };

  const handleUpdate = async (trainerData) => {
    const result = await updateTrainer(
      editingTrainer.id,
      trainerData,
      `${trainerData.name} has been updated successfully.`,
    );
    if (result) {
      setIsModalOpen(false);
      setEditingTrainer(null);
    }
  };

  const handleDelete = async () => {
    await removeTrainer(
      deleteConfirm.trainer.id,
      `${deleteConfirm.trainer.name} has been deleted successfully.`,
      deleteConfirm.trainer.organizationId,
    );
    setDeleteConfirm({ isOpen: false, trainer: null });
  };

  // Modal handlers
  const openEditModal = (trainer) => {
    setEditingTrainer(trainer);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingTrainer(null);
    setIsModalOpen(true);
  };

  // Filter options
  const filterOptions = [
    {
      key: "search",
      type: "search",
      label: t("trainersPage.searchPlaceholder"),
      placeholder: t("trainersPage.searchPlaceholder"),
      value: searchTerm,
      onChange: handleSearch,
    },
    {
      key: "status",
      type: "select",
      label: t("trainersPage.filters.status", "Durum"),
      value: filters.status,
      onChange: (value) => setFilter("status", value),
      options: isSuperAdmin()
        ? [
            { value: "all", label: t("trainersPage.filters.allStatuses", "Tüm Durumlar") },
            { value: "active", label: t("trainersPage.filters.active", "Aktif") },
            { value: "inactive", label: t("trainersPage.filters.inactive", "İnaktif") },
          ]
        : [
            { value: "active", label: t("trainersPage.filters.active", "Aktif") },
          ],
    },
  ];

  // Add organization filter for super admin in 'all' view mode
  if (isSuperAdmin() && viewMode === "all") {
    filterOptions.push({
      key: "organization",
      type: "select",
      label: t("trainersPage.organization"),
      value: filters.organization,
      onChange: (value) => setFilter("organization", value),
      options: [
        { value: "all", label: t("trainersPage.allOrganizations") },
        ...organizations.map((org) => ({
          value: org.id,
          label: org.name,
        })),
      ],
    });
  }

  // Table columns configuration - Progressive responsive hiding
  // >= 1280px: All columns | >= 1100px: hide Phone | >= 900px: hide Email | < 900px: only Name + Actions
  const columns = [
    {
      key: "trainer",
      label: t("trainersPage.table.trainer"),
      width: "2.5fr",
      render: (trainer) => (
        <div className="flex items-center space-x-3.5">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0 ${getAvatarColor(
              trainer.name,
            )}`}
          >
            {getInitials(trainer.name)}
          </div>
          <div className="min-w-0">
            <div className={`font-semibold ${colors.textPrimary} truncate`}>
              {trainer.name}
            </div>
            <div className="text-xs mt-0.5">
              {trainer.status === "active" ? (
                <span className="text-green-500 font-medium">
                  • {t("trainersPage.status.active")}
                </span>
              ) : (
                <span className="text-slate-400 font-medium font-medium">
                  • {trainer.status}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    // Phone: visible >= 1280px
    ...(windowWidth >= 1280
      ? [
          {
            key: "phone",
            label: t("trainersPage.table.phone"),
            width: "1.5fr",
            render: (trainer) => (
              <span className={`text-sm ${colors.textSecondary}`}>{trainer.phone}</span>
            ),
          },
        ]
      : []),
    // Role/Orgs: visible >= 900px
    ...(windowWidth >= 900
      ? [
          {
            key: "role_or_orgs",
            label:
              isSuperAdmin() && viewMode === "all"
                ? t("trainersPage.table.organizations")
                : t("trainersPage.table.role"),
            width: "1.5fr",
            render: (trainer) => {
              if (isSuperAdmin() && viewMode === "all") {
                return trainer.assignedOrganizations?.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {trainer.assignedOrganizations.slice(0, 2).map((org) => (
                      <span
                        key={org.id}
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/10 text-blue-500 border border-blue-500/10`}
                      >
                        {org.name}
                      </span>
                    ))}
                    {trainer.assignedOrganizations.length > 2 && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-500/10 text-slate-400`}>
                        +{trainer.assignedOrganizations.length - 2}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className={`${colors.textMuted} text-xs italic`}>
                    {t("trainersPage.notAssigned")}
                  </span>
                );
              }
              return (
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                    trainer.role === "admin"
                      ? "bg-rose-500/10 text-rose-500 border border-rose-500/20"
                      : "bg-blue-500/10 text-blue-500 border border-blue-500/20"
                  }`}
                >
                  {trainer.role}
                </span>
              );
            },
          },
        ]
      : []),
    {
      key: "actions",
      label: t("trainersPage.table.actions"),
      width: "1.25fr",
      render: (trainer) => (
        <div className="flex items-center justify-end space-x-2 w-full flex-shrink-0">
          <button
            onClick={() => navigate(`/trainers/${trainer.id}`)}
            className="btn-action bg-blue-600/10 text-blue-500 hover:bg-blue-600 hover:text-white"
            title={t("common.viewDetails")}
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() =>
              navigate("/sessions", {
                state: { selectedTrainerId: trainer.id },
              })
            }
            className="btn-action bg-green-600/10 text-green-500 hover:bg-green-600 hover:text-white"
            title={t("common.schedule")}
          >
            <Calendar className="w-4 h-4" />
          </button>
          {hasPermission("update:trainers") && (
            <button
              onClick={() => openEditModal(trainer)}
              disabled={actionLoading}
              className="btn-action bg-orange-600/10 text-orange-500 hover:bg-orange-600 hover:text-white"
              title={t("common.edit")}
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
          {hasPermission("delete:trainers") && (
            <button
              onClick={() => setDeleteConfirm({ isOpen: true, trainer })}
              disabled={actionLoading}
              className="btn-action bg-red-600/10 text-red-500 hover:bg-red-600 hover:text-white"
              title={t("common.delete")}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Mobile card renderer - Compact & Responsive
  const renderMobileCard = (trainer) => (
    <div
      className={`p-3 ${colors.tableRowHover} rounded-xl shadow-sm border ${colors.border} mb-3`}
    >
      {/* Top Row: Avatar, Info, Status */}
      <div className="flex items-start space-x-3 mb-3">
        {/* Avatar */}
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-sm flex-shrink-0 ${getAvatarColor(
            trainer.name,
          )}`}
        >
          {getInitials(trainer.name)}
        </div>

        {/* Helper for responsive truncated text */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div
              className={`font-semibold ${colors.textPrimary} text-sm truncate pr-2`}
            >
              {trainer.name}
            </div>
            {/* Status Badge */}
            <div className="flex-shrink-0">
              {trainer.status === "active" ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                  {t("trainersPage.status.active")}
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400">
                  {trainer.status}
                </span>
              )}
            </div>
          </div>

          <div className={`text-xs ${colors.textTertiary} truncate mt-0.5`}>
            {trainer.email}
          </div>
          <div className={`text-xs ${colors.textTertiary} mt-0.5`}>
            {trainer.phone}
          </div>
        </div>
      </div>

      {/* Middle Row: Role / Orgs */}
      <div className="mb-3 text-xs pl-1 sm:pl-[52px]">
        {" "}
        {/* Align with text start */}
        {isSuperAdmin() && viewMode === "all" ? (
          <div className="flex items-start">
            <span className={`${colors.textMuted} mr-1 flex-shrink-0`}>
              {t("trainersPage.table.organizations")}:
            </span>
            {trainer.assignedOrganizations?.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {trainer.assignedOrganizations.slice(0, 3).map((org) => (
                  <span
                    key={org.id}
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-100 dark:border-blue-800"
                  >
                    {org.name}
                  </span>
                ))}
                {trainer.assignedOrganizations.length > 3 && (
                  <span className="text-[10px] text-gray-500 flex items-center">
                    +{trainer.assignedOrganizations.length - 3}
                  </span>
                )}
              </div>
            ) : (
              <span className={`${colors.textSecondary} italic`}>
                {t("trainersPage.notAssigned")}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center">
            <span className={`${colors.textMuted} mr-1`}>
              {t("trainersPage.table.role")}:
            </span>
            <span className={`${colors.textSecondary} capitalize font-medium`}>
              {trainer.role}
            </span>
          </div>
        )}
      </div>

      {/* Bottom Row: Actions */}
      <div
        className={`flex flex-wrap items-center justify-between pt-2 border-t ${colors.border} gap-2`}
      >
        <button
          onClick={() => navigate(`/trainers/${trainer.id}`)}
          className="flex-1 min-w-[96px] flex justify-center items-center py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/40 transition-colors font-medium text-xs"
          title={t("common.viewDetails")}
        >
          <Eye className="w-4 h-4 mr-1.5" />
          {t("common.view")}
        </button>
        <button
          onClick={() =>
            navigate("/sessions", { state: { selectedTrainerId: trainer.id } })
          }
          className="flex-1 min-w-[96px] flex justify-center items-center py-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/40 transition-colors font-medium text-xs"
          title={t("common.schedule")}
        >
          <Calendar className="w-4 h-4 mr-1.5" />
          {t("common.schedule")}
        </button>
        {hasPermission("update:trainers") && (
          <button
            onClick={() => openEditModal(trainer)}
            disabled={actionLoading}
            className="flex-1 min-w-[96px] flex justify-center items-center py-2 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 hover:text-orange-700 dark:bg-orange-900/20 dark:text-orange-400 dark:hover:bg-orange-900/40 transition-colors disabled:opacity-50 font-medium text-xs"
            title={t("common.edit")}
          >
            <Edit className="w-4 h-4 mr-1.5" />
            {t("common.edit")}
          </button>
        )}
        {hasPermission("delete:trainers") && (
          <button
            onClick={() => setDeleteConfirm({ isOpen: true, trainer })}
            disabled={actionLoading}
            className="flex-1 min-w-[96px] flex justify-center items-center py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50 font-medium text-xs"
            title={t("common.delete")}
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            {t("common.delete")}
          </button>
        )}
      </div>
    </div>
  );

  // Empty state
  if (!isSuperAdmin() && !selectedOrganizationId) {
    return (
      <div className={`flex h-full ${colors.bgPrimary} p-6`}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-12 max-w-md">
            <div
              className={`w-16 h-16 ${colors.emptyStateBg} rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg`}
            >
              <Users className={`w-8 h-8 ${colors.emptyStateIcon}`} />
            </div>
            <h3 className={`text-lg font-bold ${colors.textPrimary} mb-2`}>
              {t("trainersPage.selectOrg.title")}
            </h3>
            <p className={colors.textTertiary}>
              {t("trainersPage.selectOrg.message")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex h-full px-3 pb-3 sm:px-4 sm:pb-4 gap-4 overflow-hidden`}>
      {/* Filters Sidebar */}
      <FilterSidebar
        isOpen={isFiltersOpen}
        onToggle={() => setIsFiltersOpen(!isFiltersOpen)}
        filters={filterOptions}
        onReset={resetFilters}
      />

      {/* Main Content */}
      <div
        className={`
        flex-1 flex flex-col overflow-hidden
        ${colors.cardBg} backdrop-blur-md bg-opacity-80
        ${colors.cardBorder} border rounded-[2rem] shadow-xl
        relative transition-all duration-500
      `}
      >
        {/* Toggle Filter Button (desktop, collapsed state) */}
        {!isFiltersOpen && (
          <div className="hidden md:block absolute top-6 left-6 z-10">
            <button
              onClick={() => setIsFiltersOpen(true)}
              className={`p-3 ${colors.bgHoverStrong} rounded-2xl shadow-sm transition-all hover:scale-110`}
            >
              <Filter className={`w-5 h-5 ${colors.textSecondary}`} />
            </button>
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 md:p-8 custom-scrollbar">
          {/* Header Area */}
          <div
            className={`flex flex-col md:flex-row justify-between mb-6 ${
              !isFiltersOpen ? "md:ml-12" : ""
            }`}
          >
            {/* Mobile Search Bar */}
            <div className="md:hidden mb-4 w-full">
              <div className="relative">
                <Search
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${colors.textMuted}`}
                />
                <input
                  type="text"
                  placeholder={t("trainersPage.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className={`
                    w-full pl-10 pr-4 py-3
                    ${colors.inputBg} ${colors.inputBorder} border
                    rounded-xl text-sm font-medium ${colors.inputText} 
                    focus:outline-none focus:ring-2 focus:ring-blue-500/50
                    shadow-sm
                  `}
                />
              </div>
            </div>

            <PageHeader
              title={t("trainersPage.title")}
              count={filteredCount}
              onRefresh={fetchTrainers}
              onAdd={hasPermission("create:trainers") ? openCreateModal : null}
              addButtonText={t("trainersPage.addTrainer")}
              addButtonTextMobile={t("trainersPage.addTrainerMobile")}
              loading={loading}
              actionLoading={actionLoading}
              noPadding={true}
            />
          </div>

          {/* Data Table */}
          <DataTable
            data={paginatedTrainers}
            columns={columns}
            loading={loading}
            actionLoading={actionLoading}
            error={error}
            successMessage={successMessage}
            onClearError={() => setError(null)}
            onClearSuccess={() => setSuccessMessage(null)}
            renderMobileCard={renderMobileCard}
            emptyState={{
              icon: Users,
              title: t("trainersPage.emptyState.title"),
              message:
                searchTerm || filters.role !== "all"
                  ? t("trainersPage.emptyState.messageSearch")
                  : t("trainersPage.emptyState.messageDefault"),
              action:
                !searchTerm && filters.role === "all"
                  ? {
                      label: t("trainersPage.addTrainer"),
                      onClick: openCreateModal,
                      icon: Plus,
                    }
                  : null,
            }}
            pagination={{
              currentPage,
              totalPages,
              rowsPerPage,
              totalItems: filteredCount,
              startIndex,
              onPageChange: handlePageChange,
              onRowsPerPageChange: handleRowsPerPageChange,
            }}
          />
        </div>
      </div>

      {/* Trainer Modal */}
      <TrainerModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTrainer(null);
        }}
        onSubmit={editingTrainer ? handleUpdate : handleCreate}
        trainer={editingTrainer}
        title={
          editingTrainer
            ? t("trainersPage.editTrainerTitle")
            : t("trainersPage.addTrainerTitle")
        }
        loading={actionLoading}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, trainer: null })}
        onConfirm={handleDelete}
        title={t("trainersPage.deleteTrainerTitle")}
        message={t("trainersPage.deleteTrainerMessage", {
          name: deleteConfirm.trainer?.name,
        })}
        confirmText={t("modals.confirmDelete")}
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
      />
    </div>
  );
};

export default TrainersPage;
